import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, ShieldCheck, Clock, Users, BarChart3, Headphones, Phone, Check } from "lucide-react";
import { BRAND } from "../mock";
import Reveal from "../components/Reveal";
import BookingWidget from "../components/BookingWidget";

const BENEFITS = [
  { icon: FileText, title: "Sammelrechnung", desc: "Alle Fahrten Ihres Unternehmens gebündelt auf einer monatlichen Rechnung – übersichtlich und buchhaltungsfreundlich." },
  { icon: ShieldCheck, title: "Feste Konditionen", desc: "Verhandelte Fixpreise für Ihre häufigsten Strecken. Kein Taxameter, keine Überraschungen." },
  { icon: Clock, title: "Priorität & Verfügbarkeit", desc: "Bevorzugte Fahrzeugverfügbarkeit – auch kurzfristig und zu Stosszeiten." },
  { icon: Users, title: "Diskrete Chauffeure", desc: "Erfahrene, mehrsprachige Chauffeure für Gäste, Kunden und Führungskräfte." },
  { icon: BarChart3, title: "Reporting & Kostenstellen", desc: "Transparente Auswertungen nach Kostenstelle, Abteilung oder Mitarbeitenden." },
  { icon: Headphones, title: "24/7 Disposition", desc: "Ein persönlicher Ansprechpartner, rund um die Uhr erreichbar." },
];

const STEPS = [
  { n: "1", title: "Firmenkonto eröffnen", desc: "Registrieren Sie Ihr Unternehmen kostenlos und hinterlegen Sie Ihre Rechnungsdaten." },
  { n: "2", title: "Fahrten buchen", desc: "Buchen Sie Transfers für Mitarbeitende und Gäste – online, telefonisch oder per E-Mail." },
  { n: "3", title: "Monatlich abrechnen", desc: "Sie erhalten eine gebündelte Rechnung mit allen Fahrten und Kostenstellen." },
];

const Firmenkunden = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="pt-[68px] min-h-screen bg-white overflow-x-hidden" data-testid="firmenkunden-page">
      {/* Hero */}
      <div className="relative bg-[#0B1533] text-white overflow-hidden">
        <img src="https://images.unsplash.com/photo-1752159400890-d906038f1b35?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533] via-[#0B1533]/85 to-[#0B1533]/70" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <p className="font-body text-sm text-[#C9A24B] font-semibold mb-3">Geschäftskunden</p>
          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Chauffeurservice für Firmenkunden.</h1>
          <p className="font-body text-base sm:text-lg text-white/70 mt-6 max-w-2xl leading-relaxed">Zuverlässige Flughafen- und Geschäftstransfers für Unternehmen jeder Grösse – mit Sammelrechnung, festen Konditionen und persönlicher Betreuung rund um die Uhr.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <a href={BRAND.emailHref} className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#1E38D8] transition-colors" data-testid="firmen-offer-btn">Angebot anfordern <ArrowRight size={16} /></a>
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 border border-white/20 text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-white/10 transition-colors"><Phone size={16} /> {BRAND.phone}</a>
          </div>
        </div>
      </div>

      {/* Booking widget */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
        <BookingWidget />
      </div>

      {/* Benefits */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <Reveal><h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-[#0B1533] max-w-2xl">Ihre Vorteile als Firmenkunde.</h2></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={i * 60}>
              <div className="h-full rounded-3xl border border-[#E9E5DE] bg-white p-6 hover:border-[#2B4BFF] hover:elev-soft transition-all">
                <span className="inline-flex w-11 h-11 rounded-2xl bg-[#EEF1FF] items-center justify-center mb-4"><b.icon size={20} className="text-[#2B4BFF]" /></span>
                <h3 className="font-heading font-semibold text-lg text-[#0B1533]">{b.title}</h3>
                <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Steps */}
      <div className="bg-[#F5F2ED] border-y border-[#E9E5DE]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <Reveal><h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl text-[#0B1533]">So funktioniert's.</h2></Reveal>
          <div className="grid sm:grid-cols-3 gap-6 mt-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 80}>
                <div className="rounded-3xl bg-white border border-[#E9E5DE] p-7 h-full">
                  <span className="inline-flex w-10 h-10 rounded-full bg-[#2B4BFF] text-white font-heading font-bold items-center justify-center">{s.n}</span>
                  <h3 className="font-heading font-semibold text-lg text-[#0B1533] mt-4">{s.title}</h3>
                  <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-[28px] bg-[#0B1533] p-8 sm:p-12 text-white flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading font-bold text-2xl sm:text-3xl">Bereit für ein Firmenkonto?</h3>
            <p className="font-body text-white/60 mt-2 max-w-xl">Kontaktieren Sie uns für ein individuelles Angebot – wir richten Ihr Konto in kürzester Zeit ein.</p>
            <ul className="mt-5 space-y-2">
              {["Kostenlose Kontoeröffnung", "Persönlicher Ansprechpartner", "Monatliche Sammelrechnung"].map((f) => (
                <li key={f} className="flex items-center gap-2 font-body text-sm text-white/80"><Check size={16} className="text-[#25D366]" /> {f}</li>
              ))}
            </ul>
          </div>
          <a href={BRAND.emailHref} className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-full px-8 py-4 hover:bg-[#1E38D8] transition-colors shrink-0">Angebot anfordern <ArrowRight size={16} /></a>
        </div>
      </div>
    </div>
  );
};

export default Firmenkunden;
